import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { SafeStorage } from '../utils/safe-storage';

/**
 * Routes a guest is allowed to reach. Everything else behind this guard belongs
 * to a host account (home, my events, event settings, billing, …) and must stay
 * out of reach until the guest actually signs in.
 *
 * `guest/:id` lives under the lazily-loaded subscription module, which is itself
 * guarded — leaving it out would lock guests out of their own invite link.
 */
const GUEST_ALLOWED_PREFIXES = [
  '/guest/',          // invite entry point
  '/event-detail',    // the event they were invited to
  '/my-photos',       // their own matched photos (restricted view)
  '/live/',           // welcome screen for a live event link
  '/app-redirect',
  '/theater-bay-event',
];

@Injectable({
  providedIn: 'root'
})
export class GuestRestrictionGuard implements CanActivate {

  constructor(private router: Router) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    try {
      const isGuestUser = SafeStorage.getItem('isGuestUser') === 'true';

      if (!isGuestUser) {
        // Not a guest — sweep up the session-scoped marker the top bar sets
        // while handing a guest over to the login page, so it can't linger
        // after the sign-in completed.
        try { sessionStorage.removeItem('isGuestUser'); } catch { /* ignore */ }
        return true;
      }

      // NOTE: do NOT treat a stored `uid` as proof the guest has since signed
      // in. Guests are signed in to Firebase *anonymously* and get a uid too
      // (guest-access.component.ts), so a uid check let every guest straight
      // through. Every real sign-in path — login.component, guest-login-popup,
      // demo-login-popup, home.component — clears `isGuestUser` itself, so the
      // flag alone is the authoritative signal.
      const url = state.url.split('?')[0];
      if (GUEST_ALLOWED_PREFIXES.some(prefix => url.startsWith(prefix))) {
        return true;
      }

      // Blocked. If something is already on screen (the usual case: browser
      // back out of event-detail into /home), just cancel — Angular restores
      // the address bar to the current route and the guest stays put, with no
      // costly re-navigation into event-detail.
      if (!this.router.navigated) {
        // Cold load straight into a host route — cancelling alone would leave a
        // blank page, so send them to the only page a guest owns.
        const isDemoUser = SafeStorage.getItem('isDemoUser') === 'true';
        // Router paths must not carry the '#': `useHash` adds it. A '/#/...'
        // target matches no route and falls through the '**' wildcard back to
        // /home — which is how guests were landing on home in the first place.
        this.router.navigate([isDemoUser ? '/demo-event' : '/event-detail/true'], { replaceUrl: true });
      }
      return false;

    } catch {
      // If localStorage is unavailable for any reason, allow navigation so the
      // user always sees the home page instead of a blank screen.
      return true;
    }
  }
}
